import { ref } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { v4 as uuidv4 } from 'uuid';
import { useAuthStore } from '@/stores/auth';
import { useEncounterFlow } from '@/common/workflowEngine/useEncounterFlow';
import { useEpisodeOfCareFlow } from '@/common/workflowEngine/useEpisodeOfCareFlow';
import { useWorkflow } from '@/common/workflowEngine/useWorkflow';
import { useProfileFlow } from '@/common/workflowEngine/useProfileFlow';
import { $httpFhirApi } from '@/common/api/httpFhir.service';
import { getUrlFromCanonical } from '@/common/core';
import { Encounter, Patient, BN_Relationship } from '@/fhirworks';
import { crud } from '@/common/crud';

export function usePortalApplication() {
    const router = useRouter();
    const route = useRoute();
    const authStore = useAuthStore();
    const { postTransaction } = useWorkflow();
    const { createEpisodeOfCare } = useEpisodeOfCareFlow();
    const isLoading = ref(false);

    /**
     * The planned episode that carries a portal application.
     *
     * An applicant needs an episode from the moment they apply: without one their state has to
     * be inferred from Patient.active, which is why that flag was being used to mean "exists".
     * The episode holds the real state, gives the admissions owner somewhere to live, and lets
     * the admissions view read one population instead of two.
     *
     * It deliberately has no program — the portal is organization-scoped and families do not
     * pick a level of care. The program encounter is created when a coordinator accepts.
     *
     * No OutcomeTools sync is fired here, even though composing this flow's transaction into
     * our own normally makes the sync the caller's responsibility (see useEpisodeOfCareFlow).
     * OutcomeTools tracks programme enrolment, and this episode has no programme yet — the
     * enrolment moment is acceptance, where AdmissionsViewApplicationDialog does sync. Firing
     * it here would also run under a portal session, where a failed sync toasts an error at
     * the family rather than at staff.
     */
    const applicationEpisode = (patientRef) => {
        // The referral arrived the moment the family submitted, so the response-time clock
        // starts here rather than when staff first open the record.
        const submittedAt = new Date().toISOString();
        return {
            patient: patientRef,
            status: 'planned',
            eligibilityStatus: 'TBD',
            managingOrganization: {
                id: authStore.organizationAccount.id,
                resourceType: 'Organization',
            },
            period: { start: submittedAt },
            referralReceivedDate: submittedAt,
        };
    };

    /**
     * Start a new application
     * @param {string} applicationType - 'self' or 'other'
     * Creates patient if needed, establishes relationship, creates encounter, and opens form filler
     */
    const startApplication = async (applicationType) => {
        isLoading.value = true;

        let patient = authStore.patient;

        try {
            const portalPacketId = authStore.organizationAccount?.portalPacketId;
            if (!portalPacketId) {
                console.error('No portal packet configured for this organization');
                isLoading.value = false;
                return;
            }

            // Read the packet before creating anything. It is the only step that can bail out,
            // and doing it first means we never leave a half-created applicant behind.
            const packetResponse = await $httpFhirApi.get(`/List/${portalPacketId}`);
            const formPacket = packetResponse.data;

            if (!formPacket) {
                console.error('No forms found in the portal packet');
                isLoading.value = false;
                return;
            }

            if (applicationType === 'other') {
                // Create a new patient resource for someone else
                const patientId = uuidv4();
                const mrn = uuidv4().substring(24); // Last 8 characters of UUID for MRN

                const newPatient = new Patient({
                    id: patientId,
                    // Created inactive and flipped alongside the episode below. The episode is what
                    // justifies an active patient — "must be active if they have a planned episode"
                    // — and it cannot be written in this bundle, because the access policy that
                    // authorises it reads a relationship this same bundle is still creating. Were
                    // the patient active from here, a failure in the second transaction would leave
                    // exactly what #5116 exists to prevent: an active patient with no episode.
                    active: false,
                    inquiryStatus: 'pending',
                    gender: 'unknown',
                    name: [
                        {
                            use: 'official',
                        },
                    ],
                    identifier: [
                        {
                            type: {
                                coding: [
                                    {
                                        system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
                                        code: 'MR',
                                        display: 'Medical record number',
                                    },
                                ],
                            },
                            system: 'https://' + authStore.account.fhirApiUri + '/patients',
                            value: mrn,
                        },
                    ],
                    managingOrganization: {
                        id: authStore.organizationAccount.id,
                        resourceType: 'Organization',
                    },
                });

                const relationship = new BN_Relationship({
                    subject: {
                        id: authStore.patient.id,
                        resourceType: 'Patient',
                    },
                    related: {
                        id: patientId,
                        resourceType: 'Patient',
                    },
                    active: true,
                    period: {
                        start: new Date().toISOString(),
                    },
                });
                const inverseRelationship = new BN_Relationship({
                    subject: {
                        id: patientId,
                        resourceType: 'Patient',
                    },
                    related: {
                        id: authStore.patient.id,
                        resourceType: 'Patient',
                    },
                    portalAccess: true,
                    active: true,
                    period: {
                        start: new Date().toISOString(),
                    },
                });

                // Create the patient and the relationships that give this portal user access to
                // them. The episode is deliberately NOT in this bundle: the access policy that
                // lets a portal user open an episode for someone else reads the portalAccess
                // BN_Relationship out of the database, and a row written by a sibling entry of
                // the same transaction is not visible to it yet, so the episode is rejected.
                const patientTransaction = [crud.post(newPatient), crud.post(relationship), crud.post(inverseRelationship)];

                const patientResponse = await postTransaction(patientTransaction);
                patient = patientResponse?.find((item) => item.resourceType === 'Patient');

                if (!patient) {
                    console.error('Failed to create patient');
                    isLoading.value = false;
                    return;
                }

                // Refresh related persons to include the new patient
                await authStore.refreshRelatedPersons();

                // Set the new patient as the viewing patient
                authStore.setPortalViewingPatientId(patient.id);
            } else if (!patient?.id) {
                // For 'self' application, ensure patient exists
                isLoading.value = false;
                return;
            }

            // Get the patient reference (either current user's patient or newly created one)
            const patientRef = { id: patient.id, resourceType: 'Patient' };

            // Create encounter directly without requiring a program
            const encounterResource = new Encounter({
                id: uuidv4(),
                status: 'in-progress',
                class: { code: 'AMB', display: 'ambulatory' },
                type: [{ coding: [{ code: 'portalPacket', display: 'Portal packet' }] }],
                subject: patientRef,
                period: { start: new Date().toISOString() },
                location: [{ location: { display: 'Unknown' } }],
            });

            // Only create the encounter - addForm will create the necessary tasks
            let transaction = [crud.post(encounterResource)];

            // Both paths create the applicant's episode here, once the relationships the access
            // policy checks are committed, and both patch the patient in the same bundle so the
            // patient's state and the episode that justifies it cannot diverge.
            transaction = await createEpisodeOfCare(applicationEpisode(patientRef), transaction);
            if (applicationType === 'self') {
                patient.inquiryStatus = 'pending';
            } else {
                patient.active = true;
            }
            transaction.push(crud.patch(patient));
            const response = await postTransaction(transaction);

            const encounter = response?.find((item) => item.resourceType === 'Encounter');
            if (!encounter) {
                console.error('Failed to create portal packet encounter');
                isLoading.value = false;
                return;
            }

            // Add forms from the packet to the encounter
            const { addForm } = useEncounterFlow();
            // Managed forms this tenant inactivated for its own system must be skipped (compared on base urls).
            const suppressedCatalogUrls = await useProfileFlow().getSuppressedCatalogUrls();
            let formTasks = null;
            for (const entry of formPacket.entry || []) {
                const formUrl = entry.item?.uri;
                if (!formUrl) continue;
                if (suppressedCatalogUrls.has(getUrlFromCanonical(formUrl).url)) continue;

                const workflowData = {
                    encounter: encounter,
                    form: formUrl,
                    filler: {
                        id: authStore.patient?.id,
                        resourceType: authStore.patient?.resourceType,
                    },
                    isPortal: true,
                };

                const formResponse = await addForm(workflowData);
                if (!formTasks) {
                    formTasks = formResponse;
                }
            }

            // Find the main task and open the form filler
            const mainTask = formTasks?.find((resource) => resource.resourceType === 'Task' && !resource.partOf?.length);
            if (mainTask) {
                router.push({
                    name: route.name || 'dashboard',
                    params: { ...route.params, activeDialog: 'form', activeId: mainTask.id },
                });
            }
        } catch (error) {
            console.error('Error starting application:', error);
        } finally {
            isLoading.value = false;
        }
    };

    /**
     * Convenience method to start a new application for someone else
     */
    const startNewApplication = async () => {
        await startApplication('other');
    };

    return {
        isLoading,
        startApplication,
        startNewApplication,
    };
}
